/**
 * External Imports
 */
import { FC, Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import {
  logListClick,
  logReportsClick,
  logTaskClick
} from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';

/**
 * Style Imports
 */
import { SearchInput, SearchContainer } from './SearchField.styles';

interface SearchFieldProps {
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const SearchField: FC<SearchFieldProps> = ({ searchTerm, setSearchTerm }) => {
  const { '*': view } = useParams();

  const { logTrackingEvent } = useAnalyticsApi();

  const dbSearchTerm = useDebounceValue(searchTerm.trim(), 350);

  const handleSearch = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchFocus = useCallback(() => {
    switch (view) {
      case 'census':
      case 'list/user':
      case 'list/delegate':
      case 'list/adhoc':
      case 'csc/list':
      case 'cned/list':
        logTrackingEvent(logListClick('search', view));
        break;
      case 'issues':
      case 'cned/issues':
      case 'feedback':
      case 'cned/feedback':
      case 'stoplight':
        logTrackingEvent(logTaskClick(view, 'search'));
        break;
      case 'employee':
        logTrackingEvent(logReportsClick('employee-rounds', 'search'));
        break;
      case 'employee/rounds':
      case 'employee/frequency':
      case 'patient/rounds':
      case 'patient/census':
        logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'search'));
        break;
      default:
        console.log('No view for event');
    }
  }, [view, logTrackingEvent, logListClick, logTaskClick, logReportsClick]);

  const setSearchSuffix = () => {
    if (view?.includes('list')) {
      return 'by employee name or 3/4 ID';
    }

    switch (view) {
      case 'areas':
        return 'by ID or name';
      case 'census':
      case 'patient/census':
        return 'by name or MRN';
      case 'discharges':
        return 'by patient name';
      case 'users':
        return 'by name or 3/4 ID';
      case 'question-bank':
        return 'by title';
      case 'patient/rounds':
        return 'by patient name or MRN';
      case 'employee':
      case 'employee/rounds':
      case 'employee/frequency':
        return 'by employee name or 3/4 ID';
      case 'templates':
        return 'for a question';
      case 'feedback':
      case 'cned/feedback':
      case 'issues':
      case 'cned/issues':
      case 'stoplight':
        return '- enter minimum 3 characters';
      default:
        return 'by ID';
    }
  };

  useEffect(() => {
    if (dbSearchTerm.length > 0) handleSearchFocus();
  }, [dbSearchTerm]);

  return (
    <SearchContainer id="Search-Field">
      <SearchInput
        id="search-input-field"
        data-testid="search-input"
        label=""
        required
        suffix={setSearchSuffix()}
        value={searchTerm}
        width={view?.includes('employee') ? 345 : undefined}
        onNeuChange={handleSearch}
      />
    </SearchContainer>
  );
};

export default SearchField;
